import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSessionStorage } from 'react-use';
import { DateTime } from 'luxon';
import { SmartDiaryEntry } from 'modules/smartDiary/smartDiaryNotes/type';
import { useMeContext } from 'modules/me';
import { Period, TimeSignal } from 'graphql/types';
import { useSmartTimesheetContext } from '../SmartTimesheetContextProvider';
import { SmartDiaryListTabValue } from './useSmartDiaryTabs';

export type UseSmartDiaryUpdateStateProps = {
  notAppliedEntries: Record<string, SmartDiaryEntry[]>;
  tabValue: SmartDiaryListTabValue;
  smartDiarySyncCount: number;
};

export type TabIndicatorState = {
  entries: {
    [id: string]: {
      lastModifiedTimes: string[];
    };
  };
};
type ViewedNotAppliedEntries = Map<string, TabIndicatorState | null>;

export const getDaysOfPeriod = (currentPeriod: Period | null): string[] => {
  if (!currentPeriod) {
    return [];
  }

  const startDate = DateTime.fromFormat(currentPeriod.startDate, 'yyyy-MM-dd');
  const endDate = DateTime.fromFormat(currentPeriod.endDate, 'yyyy-MM-dd');
  let diffInDays = endDate.diff(startDate, 'days').as('days');

  const daysOfPeriod = [];
  while (diffInDays >= 0) {
    const date = startDate.plus({ days: diffInDays }).toFormat('yyyy-MM-dd');
    daysOfPeriod.push(date);
    diffInDays--;
  }

  return daysOfPeriod.reverse();
};

export const createTabIndicatorState = (
  smartEntries: SmartDiaryEntry[]
): TabIndicatorState => {
  const objectTuples = smartEntries.map((smartEntry: SmartDiaryEntry) => [
    smartEntry.id,
    {
      lastModifiedTimes: smartEntry.timeSignals?.map(
        (signal: TimeSignal) => signal.modificationTime
      )
    }
  ]);

  return {
    entries: Object.fromEntries(objectTuples)
  };
};

export const hasPeriodChanged = (
  previousPeriod: Period | null,
  currentPeriod: Period | null
): boolean =>
  previousPeriod?.startDate !== currentPeriod?.startDate ||
  previousPeriod?.endDate !== currentPeriod?.endDate;

export const hasTabChanged = (
  previousTab: SmartDiaryListTabValue,
  currentTab: SmartDiaryListTabValue
): boolean => previousTab !== currentTab;

export const doesTabHaveNotAppliedEntries = (
  tabValue: SmartDiaryListTabValue
): boolean =>
  tabValue === SmartDiaryListTabValue.NOT_APPLIED ||
  tabValue === SmartDiaryListTabValue.ALL;

export const notAppliedEntriesHasUpdate = (
  notAppliedEntries: Record<string, SmartDiaryEntry[]>,
  viewedNotAppliedEntries: ViewedNotAppliedEntries
): boolean => {
  return Object.entries(notAppliedEntries).some(([date, smartDiaryEntries]) => {
    const viewedForDate = viewedNotAppliedEntries.get(date);

    return smartDiaryEntries.some((smartDiaryEntry) => {
      const viewedSmartEntry = viewedForDate?.entries[smartDiaryEntry.id];
      if (!viewedSmartEntry) {
        return true;
      }

      const modificationTimes = smartDiaryEntry.timeSignals?.map(
        (sig) => sig.modificationTime
      );
      if (modificationTimes) {
        return modificationTimes.some(
          (lastModifiedTime: string) =>
            !viewedSmartEntry.lastModifiedTimes.includes(lastModifiedTime)
        );
      }

      return false;
    });
  });
};

export const useViewedNotAppliedEntries = (): [
  ViewedNotAppliedEntries,
  (value: ViewedNotAppliedEntries) => void
] => {
  const me = useMeContext();
  const [
    viewedNotAppliedEntries,
    setViewedNotAppliedEntries
  ] = useSessionStorage<string>(
    `${me?.id}_viewedNotAppliedEntriesMap`,
    JSON.stringify(Array.from(new Map().entries()))
  );

  const setViewedNotAppliedEntriesWithSerialization = useCallback(
    (value: ViewedNotAppliedEntries) => {
      return setViewedNotAppliedEntries(
        JSON.stringify(Array.from(value.entries()))
      );
    },
    [setViewedNotAppliedEntries]
  );
  return useMemo(
    () => [
      new Map(JSON.parse(viewedNotAppliedEntries)),
      setViewedNotAppliedEntriesWithSerialization
    ],
    [viewedNotAppliedEntries, setViewedNotAppliedEntriesWithSerialization]
  );
};

export const useSmartDiaryTabsUpdateState = ({
  notAppliedEntries,
  tabValue,
  smartDiarySyncCount
}: UseSmartDiaryUpdateStateProps): boolean => {
  const me = useMeContext();
  const { currentPeriod } = useSmartTimesheetContext();
  const [
    viewedNotAppliedEntries,
    setViewedNotAppliedEntries
  ] = useViewedNotAppliedEntries();
  const [hasTabUpdate, setHasTabUpdate] = useSessionStorage<boolean>(
    `${me?.id}_hasTabUpdate`,
    false
  );
  const [previousPeriod, setPreviousPeriod] = useSessionStorage<Period | null>(
    `${me?.id}_previousPeriod`,
    currentPeriod
  );
  const [
    previousSmartDiarySyncCount,
    setPreviousSmartDiarySyncCount
  ] = useSessionStorage<number>(
    `${me?.id}_smartDiarySyncCount`,
    smartDiarySyncCount
  );

  const previousTab = useRef<SmartDiaryListTabValue>(tabValue);

  const updateLastViewed = useCallback(() => {
    const daysOfPeriod = getDaysOfPeriod(currentPeriod);

    daysOfPeriod.forEach((date: string) => {
      const entriesForDate = notAppliedEntries[date];
      if (!entriesForDate) {
        viewedNotAppliedEntries.delete(date);
      } else {
        viewedNotAppliedEntries.set(
          date,
          createTabIndicatorState(entriesForDate)
        );
      }
    });

    setViewedNotAppliedEntries(viewedNotAppliedEntries);
  }, [
    currentPeriod,
    setViewedNotAppliedEntries,
    viewedNotAppliedEntries,
    notAppliedEntries
  ]);
  const onViewingTab = useCallback(() => {
    setHasTabUpdate(false);
    updateLastViewed();
  }, [setHasTabUpdate, updateLastViewed]);

  useEffect(() => {
    const notAppliedHasUpdate = notAppliedEntriesHasUpdate(
      notAppliedEntries,
      viewedNotAppliedEntries
    );

    if (hasPeriodChanged(previousPeriod, currentPeriod)) {
      setPreviousPeriod(currentPeriod);
    }

    const numberOfNotAppliedEntries = Object.values(notAppliedEntries).flat()
      .length;
    if (
      (hasPeriodChanged(previousPeriod, currentPeriod) &&
        !notAppliedHasUpdate) ||
      numberOfNotAppliedEntries === 0
    ) {
      setHasTabUpdate(false);
    }

    if (!hasTabUpdate && notAppliedHasUpdate) {
      setHasTabUpdate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notAppliedEntries, currentPeriod]);

  useEffect(() => {
    if (
      hasTabChanged(previousTab.current, tabValue) &&
      doesTabHaveNotAppliedEntries(previousTab.current)
    ) {
      onViewingTab();
    }
    previousTab.current = tabValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  useEffect(() => {
    if (
      previousSmartDiarySyncCount !== smartDiarySyncCount &&
      !hasPeriodChanged(previousPeriod, currentPeriod) &&
      doesTabHaveNotAppliedEntries(previousTab.current)
    ) {
      onViewingTab();
    }

    setPreviousSmartDiarySyncCount(smartDiarySyncCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartDiarySyncCount]);

  return hasTabUpdate;
};
